import Close from "@mui/icons-material/Close";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Save from "@mui/icons-material/Save";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useReviewerIssues, useDueDateWarningsByUserId } from "contexts/SubmissionFormContext";
import { SignatureType, Country } from "generated-graphql/graphql";
import { SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { DateTime } from "luxon";
import React, { useState, useMemo, useRef, useEffect } from "react";

import { ReviewerSignerPicker } from "./ReviewerSignerPicker";

interface WetInkSignerDialogProps {
  initialSigner: SubmissionReviewerSigner | null;
  signatureType: SignatureType;
  open: boolean;
  onClose: () => void;
  onSave: (signer: SubmissionReviewerSigner) => void;
  onChange?: (signer: SubmissionReviewerSigner | null) => void;
}

export function SignerDialog({
  open,
  onClose,
  onSave,
  onChange,
  initialSigner,
  signatureType,
}: WetInkSignerDialogProps) {
  // Store initial snapshot for cancel/revert
  const initialSnapshotRef = useRef(initialSigner);

  const [signer, setSigner] = useState<SubmissionReviewerSigner | null>(initialSigner);
  const [signerNameOverride, setSignerNameOverride] = useState(
    initialSigner?.signerNameOverride ?? ""
  );
  const [signerEmailOverride, setSignerEmailOverride] = useState(
    initialSigner?.signerEmailOverride ?? ""
  );

  const [streetAddress1, setStreetAddress1] = useState(initialSigner?.streetAddress1 ?? "");
  const [streetAddress2, setStreetAddress2] = useState(initialSigner?.streetAddress2 ?? "");
  const [city, setCity] = useState(initialSigner?.city ?? "");
  const [state, setState] = useState(initialSigner?.state ?? "");
  const [zip, setZip] = useState(initialSigner?.zip ?? "");
  const [country, setCountry] = useState(initialSigner?.country ?? "");
  const [dueDate, setDueDate] = useState<DateTime | null>(initialSigner?.dueDate ?? null);

  const issues = useReviewerIssues();
  const dueDateWarningsByUserId = useDueDateWarningsByUserId();
  const signerIssues = useMemo(
    () =>
      signer?.id ? issues.filter((i) => i.schemaStack?.some((s) => s.name === signer.id)) : [],
    [issues, signer?.id]
  );

  const dueDateIssues = useMemo(
    () => signerIssues.filter((i) => i.key === "dueDate"),
    [signerIssues]
  );

  const emailOverrideIssues = useMemo(
    () => signerIssues.filter((i) => i.key === "signerEmailOverride"),
    [signerIssues]
  );

  const signerDueDateWarning = useMemo(
    () => (signer?.id ? dueDateWarningsByUserId[signer.id] : undefined),
    [dueDateWarningsByUserId, signer?.id]
  );

  const hasSignerDueDateWarning = dueDateIssues.length === 0 && Boolean(signerDueDateWarning);

  // Notify parent when changes happen - using ref to avoid adding to deps
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!onChangeRef.current) return;

    if (signer?.id) {
      const updatedSigner = {
        ...signer,
        signerNameOverride: signerNameOverride || undefined,
        signerEmailOverride: signerEmailOverride || undefined,
        streetAddress1: streetAddress1 || undefined,
        streetAddress2: streetAddress2 || undefined,
        city: city || undefined,
        state: state || undefined,
        zip: zip || undefined,
        country: country || undefined,
        dueDate: dueDate || undefined,
        isSigner: true,
      };
      onChangeRef.current(updatedSigner);
    } else {
      onChangeRef.current(null);
    }
  }, [
    signer,
    signerNameOverride,
    signerEmailOverride,
    streetAddress1,
    streetAddress2,
    city,
    state,
    zip,
    country,
    dueDate,
  ]);

  const handleCancel = () => {
    // Revert to initial snapshot
    if (onChange) {
      onChange(initialSnapshotRef.current);
    }
    onClose();
  };

  const handleSave = () => {
    if (signer?.id && (signatureType === SignatureType.WetInk ? streetAddress1.trim() : true)) {
      onSave({
        ...signer,
        signerNameOverride: signerNameOverride || undefined,
        signerEmailOverride: signerEmailOverride || undefined,
        streetAddress1: streetAddress1 || undefined,
        streetAddress2: streetAddress2 || undefined,
        city: city || undefined,
        state: state || undefined,
        zip: zip || undefined,
        country: country || undefined,
        dueDate: dueDate || undefined,
        isSigner: true,
      });
    }
  };

  const canSave =
    signer?.id &&
    emailOverrideIssues.length === 0 &&
    (signatureType === SignatureType.WetInk ? streetAddress1.trim() : true);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="div">
              Add {prettyPrintSignatureType(signatureType)} Signer
            </Typography>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          <Stack spacing={3}>
            {/* Description */}
            <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.66 }}>
              Add a {prettyPrintSignatureType(signatureType).toLowerCase()} signer to the
              submission. This person cannot be a reviewer.
            </Typography>

            {/* Form Fields */}
            <Stack spacing={3}>
              <Box sx={{ "& .MuiFormHelperText-root": { display: "none" } }}>
                <ReviewerSignerPicker
                  mode="signer"
                  onChange={(newSigner) => {
                    if (!newSigner) {
                      setSigner(null);
                      return;
                    }
                    setSigner({ ...signer, ...newSigner });
                  }}
                  value={signer}
                />
              </Box>

              <Box sx={{ maxWidth: 280 }}>
                <DatePicker
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>Due date</span>
                      <Tooltip
                        placement="top"
                        title={`BoldSign requires due dates at least 1 day in the future based on UTC. Today's UTC date is ${DateTime.utc().toFormat(
                          "MMM d, yyyy"
                        )}, so the earliest allowed date is ${DateTime.utc()
                          .plus({ days: 1 })
                          .toFormat("MMM d, yyyy")}.`}
                      >
                        <InfoOutlined sx={{ fontSize: 14 }} />
                      </Tooltip>
                    </Stack>
                  }
                  value={dueDate}
                  onChange={(newValue) => setDueDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: dueDateIssues.length > 0,
                      helperText: `Earliest allowed: ${DateTime.utc()
                        .plus({ days: 1 })
                        .toFormat("MMM d, yyyy")} (UTC)`,
                      sx: {
                        ...(hasSignerDueDateWarning && {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "warning.main",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "warning.dark",
                          },
                          "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "warning.main",
                          },
                          "& .MuiFormHelperText-root": {
                            color: "warning.dark",
                          },
                        }),
                      },
                    },
                  }}
                />
              </Box>
              <TextField
                label="Full Name"
                value={signerNameOverride}
                onChange={(e) => setSignerNameOverride(e.target.value)}
                fullWidth
                helperText="Leave blank to use signer's account name"
              />
              <TextField
                label="BoldSign Email Override"
                value={signerEmailOverride}
                onChange={(e) => setSignerEmailOverride(e.target.value)}
                fullWidth
                error={emailOverrideIssues.length > 0}
                helperText={
                  emailOverrideIssues.length > 0
                    ? emailOverrideIssues[0].message
                    : "Leave blank to use signer's account email"
                }
              />
              {signatureType === SignatureType.WetInk && (
                <Stack spacing={3}>
                  <TextField
                    label="Street Address 1 *"
                    value={streetAddress1}
                    onChange={(e) => setStreetAddress1(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Street Address 2"
                    value={streetAddress2}
                    onChange={(e) => setStreetAddress2(e.target.value)}
                    fullWidth
                  />
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="City"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="State"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      sx={{ width: "30%" }}
                    />
                    <TextField
                      label="ZIP"
                      value={zip}
                      onChange={(e) => setZip(e.target.value)}
                      sx={{ width: "30%" }}
                    />
                  </Stack>
                  <FormControl fullWidth>
                    <InputLabel id="country-label">Country</InputLabel>
                    <Select
                      labelId="country-label"
                      label="Country"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                    >
                      <MenuItem value={Country.Usa}>United States</MenuItem>
                      <MenuItem value={Country.Canada}>Canada</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              )}
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancel} variant="text">
            CANCEL
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={!canSave} startIcon={<Save />}>
            SAVE
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

function prettyPrintSignatureType(signatureType: SignatureType) {
  switch (signatureType) {
    case SignatureType.Digital:
      return "Digital";
    case SignatureType.WetInk:
      return "Wet-Ink";
  }

  return "";
}
